import { upsDestinationDetails } from "utils/upsDestinationDetails";



// specialFeeTaxUtils.ts
// All helper types & functions in one place, ready to import into your checkout screens.
// No React imports; pure TypeScript utilities.
import { safeConsole } from "utils/envUtils";
import { handleApiError } from "utils/errorHandler";


// =========================
// Types
// =========================
import { getAuthCookie } from "../utils/authCookies";
export type SpecialFee = {
    id: string;
    name: string;
    amount: number;
    taxable: boolean;
    available_on: string[]; // e.g. ["checkoutPage"]
    required: boolean;
    status: boolean;
    limit_to_agents: string[]; // userIds
    limit_to_brokerages: string[]; // brokerageIds (can be same id domain as user)
    limit_to_groups: string[]; // group ids
};

export type CustomerGroup = {
    _id?: string;
    id?: string;
    name?: string;
    // optional, in case your API includes members inline
    members?: Array<string | { id?: string; _id?: string }>;
    users?: Array<string | { id?: string; _id?: string }>;
};

export type UiFee = {
    id: string;
    title: string;
    subtitle?: string;
    amount: number;
    required?:boolean;
    note?: string;
    icon?: string;
    // extra fields (safe for TS structural typing; UI can ignore them)
    taxable?: boolean;
    noteTitle?: string;
    noteText?: string;
};

export type CartItemInput = {
    sku: string; // itemId in TaxCloud
    price: number;
    quantity: number;
};

export type TaxCloudAddress = {
    addressLine1: string;
    city: string;
    state: string; // 2-letter code preferred
    zip: string;
};

export type CartLineInput = {
    itemId: string;
    price: number;
    quantity: number;
    title?: string;  
    sku?: string;
    shipping_name?: string;
    itemType: "default" | "shipping" | "special_fee";
};

const supplierId = localStorage.getItem("supplierId") ;
const warehouseuserId = getAuthCookie("userId");
// =========================
// Fetchers (plain; call these from your components)
// =========================
export async function fetchSpecialFees(token: string): Promise<SpecialFee[]> {
    const userId = getAuthCookie("userId");
    const licenseType = getAuthCookie("licenseType");
    const userName = getAuthCookie("userName");
    try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/special-fees/created-by/${supplierId}`, {
            method: "GET",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        // console.log(res,"special-fee dataa")
        if (!res.ok) throw new Error("Failed to fetch special fees");
        const data = await res.json();
        return Array.isArray(data) ? (data as SpecialFee[]) : [];
    } catch (error) {
        await handleApiError(error, {
            module: "other",
            action: "SpecialFeeTaxCloud_fetch_special_fees",
            userId: userId || undefined,
            licenseType: licenseType || undefined,
            userName: userName || undefined,
            url: `${process.env.REACT_APP_API_URL}/special-fees/created-by/${supplierId}`,
            method: "GET"
        }, token);
        throw error;
    }
}

export async function fetchGroups(token: string): Promise<CustomerGroup[]> {
    const userId = getAuthCookie("userId");
    const licenseType = getAuthCookie("licenseType");
    const userName = getAuthCookie("userName");
    try {
        // If your API is actually /customer-groups, just switch the path here.
        const res = await fetch(`${process.env.REACT_APP_API_URL}/groups`, {
            method: "GET",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        if (!res.ok) throw new Error("Failed to fetch groups");
        const data = await res.json();
        return Array.isArray(data) ? (data as CustomerGroup[]) : [];
    } catch (error) {
        await handleApiError(error, {
            module: "other",
            action: "SpecialFeeTaxCloud_fetch_groups",
            userId: userId || undefined,
            licenseType: licenseType || undefined,
            userName: userName || undefined,
            url: `${process.env.REACT_APP_API_URL}/groups`,
            method: "GET"
        }, token);
        throw error;
    }
}

// =========================
// Group membership checks (two strategies)
// =========================
export async function isUserInGroupViaApi(
    token: string,
    groupId: string,
    userId: string
): Promise<boolean> {
    const licenseType = getAuthCookie("licenseType");
    const userName = getAuthCookie("userName");
    try {
        // Adjust if your backend exposes a different route
        const res = await fetch(
            `${process.env.REACT_APP_API_URL}/customer-groups/${groupId}/users/${userId}/exists`,
            { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
        );
        if (!res.ok) return false;
        const data = await res.json();
        return !!(data && (data.exists || data.member === true));
    } catch (error) {
        await handleApiError(error, {
            module: "other",
            action: "SpecialFeeTaxCloud_is_user_in_group_via_api",
            userId: userId || undefined,
            licenseType: licenseType || undefined,
            userName: userName || undefined,
            url: `${process.env.REACT_APP_API_URL}/customer-groups/${groupId}/users/${userId}/exists`,
            method: "GET",
            additionalInfo: {
                groupId: groupId || null
            }
        }, token);
        return false;
    }
}

export function isUserInGroupFromList(
    groups: CustomerGroup[] | undefined,
    groupId: string,
    userId: string
): boolean {
    if (!groups || !groupId || !userId) return false;
    const g = groups.find((x) => (x._id || x.id) === groupId);
    if (!g) return false;
    const list = Array.isArray(g.members)
        ? g.members
        : Array.isArray(g.users)
            ? g.users
            : [];
    return list.some((m: any) => {
        if (typeof m === "string") return m === userId;
        const mid = m?.id || m?._id;
        return mid === userId;
    });
}

// =========================
// Eligibility & mapping
// =========================
export async function pickEligibleSpecialFee(
    fees: SpecialFee[],
    userId: string,
    token: string,
    options?: {
        groups?: CustomerGroup[];
        checkUserInGroup?: (groupId: string) => Promise<boolean>; // custom checker override
        requireAvailableOn?: string; // e.g., "checkoutPage"
    }
): Promise<SpecialFee | null> {
    if (!userId || !Array.isArray(fees) || fees.length === 0) return null;

    const targetPage = options?.requireAvailableOn || "checkoutPage";
    const list = fees.filter((f) => (f.available_on || []).includes(targetPage));
    for (const fee of list) {
        // Direct matches
        if (Array.isArray(fee.limit_to_agents) && fee.limit_to_agents.includes(userId)) return fee;
        if (Array.isArray(fee.limit_to_brokerages) && fee.limit_to_brokerages.includes(userId)) return fee;

        // Group matches
        if (Array.isArray(fee.limit_to_groups) && fee.limit_to_groups.length) {
            for (const gid of fee.limit_to_groups) {
                let inGroup = false;
                if (options?.checkUserInGroup) inGroup = await options.checkUserInGroup(gid);
                else inGroup = isUserInGroupFromList(options?.groups || [], gid, userId);
                if (inGroup) return fee;
            }
        }
    }
    return null;
}

export function mapSpecialFeeToUiFee(fee: SpecialFee): UiFee {
    return {
        id: fee.id || "special",
        title: fee.name,
        subtitle: fee.required ? "(Required)" : "(Optional)",
        amount: Number(fee.amount || 0),
        note: fee.required
            ? "This fee is required for this order."
            : "You can include this optional fee.",
        icon: "rocket",
        taxable: !!fee.taxable,
        noteTitle: fee.required ? "Required Fee" : "Optional Fee",
        noteText: fee.taxable ? "Tax will also be calculated for this fee." : "No tax on this fee.",
    };
}

// =========================
// TaxCloud: build lines & addresses
// =========================
export function buildTaxCloudLines(
    items: CartItemInput[],
    selectedShipping?: { id?: string; cost?: number,title?: string } | null,
    selectedFee?: UiFee | null
): CartLineInput[] {
    const lines: CartLineInput[] = [];

    // 1) Product lines
    for (const it of items || []) {
        lines.push({
            itemId: String(it.sku || "SKU"),
            price: Number(it.price || 0),
            quantity: Number(it.quantity || 1),
            itemType: "default",
        });
    }

    // 2) Shipping line (manual selection)
    if (selectedShipping && Number(selectedShipping.cost) > 0) {
        lines.push({
            itemId: selectedShipping.title || "Shipping",
            price: Number(selectedShipping.cost || 0),
            quantity: 1,
            itemType: "shipping",
        });
    }

    // 3) Special fee line
    if (selectedFee && Number(selectedFee.amount) > 0) {
        lines.push({
            itemId: selectedFee.title || "SPECIAL",
            price: Number(selectedFee.amount || 0),
            quantity: 1,
            itemType: "special_fee",
        });
    }

    return lines;
}

export function toTaxCloudAddressFromObject(obj: any): TaxCloudAddress | null {
    if (!obj) return null;
    // try common shapes
    const a = obj.address_details || obj.address || obj || obj.shippingAddress;
    // console.log("toTaxCloudAddressFromObject", a);
    // if (!a) return null;
    const addressLine1 = a.addressLine1 || a.line1 || a.address1 || "";
    const city = a.city || a.cityCode || "";
    const state = a.stateCode || "";
    const zip = a.zip || a.postalCode || a.postcode || "";
    // console.log(addressLine1, city, state, zip, "toTaxCloudAddressFromObject12233");
    // if (!addressLine1 || !city || !state || !zip) return null;
    return { addressLine1, city, state, zip };
}

function addressFromTaxCloudOrigin(origin: any): TaxCloudAddress | null {
    if (!origin) return null;
    return {
        addressLine1: origin.addressLine1 || "",
        city: origin.city || origin.cityCode || "",
        state: origin.stateCode || origin.state || "",
        zip: origin.zip || "",
    };
}

// export async function fetchDefaultWarehouseAddress(token: string): Promise<TaxCloudAddress | null> {
//     const userId = getAuthCookie("userId");
//     const licenseType = getAuthCookie("licenseType");
//     const userName = getAuthCookie("userName");
//     try {
//         const currentSupplierId = localStorage.getItem("supplierId") || supplierId || warehouseuserId;
//         if (!currentSupplierId) return null;

//         const res = await fetch(`${process.env.REACT_APP_API_URL}/taxcloud/config/created-by/${currentSupplierId}`, {
//             method: "GET",
//             headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
//         });

//         if (!res.ok) return null;

//         const data = await res.json();
//         const configs: any[] = Array.isArray(data) ? data : [];
        
//         // Get the first config's origin_address
//         const config = configs.find((c) => c?.origin_address) || configs[0];
//         const originAddress = config?.origin_address;

//         return originAddress ? addressFromTaxCloudOrigin(originAddress) : null;
//     } catch (error) {
//         const currentSupplierId = localStorage.getItem("supplierId") || supplierId || warehouseuserId;
//         await handleApiError(error, {
//             module: "other",
//             action: "SpecialFeeTaxCloud_fetch_default_warehouse_address",
//             userId: userId || undefined,
//             licenseType: licenseType || undefined,
//             userName: userName || undefined,
//             url: `${process.env.REACT_APP_API_URL}/taxcloud/config/created-by/${currentSupplierId}`,
//             method: "GET"
//         }, token);
//         return null;
//     }
// }

// =========================
// TaxCloud: upsert cart
// =========================
export async function upsertTaxCloudCart(params: {
    token: string;
    cartId: string;
    customerId: string;
    // origin: TaxCloudAddress;
    destination: TaxCloudAddress;
    cartItems: CartLineInput[];
}): Promise<any> {
    const { token, cartId, customerId, destination, cartItems } = params;
    const userId = getAuthCookie("userId");
    const licenseType = getAuthCookie("licenseType");
    const userName = getAuthCookie("userName");
    const payload = { cartId, customerId, destination, cartItems };
    
    safeConsole.log("upsertTaxCloudCarcheckert", cartId, customerId, destination, cartItems);  
    // console.log("salesordertaxcloudapicalls inside taxcloud/carts/created-by", supplierId);
    if(!supplierId) return null;
    // console.log("salesordertaxcloudapicalls inside taxcloud/carts/created-by data fetchiing success");
    
    try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/taxcloud/carts/created-by/${supplierId}`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        });

        const raw = await res.text();
        let json: any = null;
        try {
            json = raw ? JSON.parse(raw) : null;
        } catch {
            json = null;
        }

        if (!res.ok) {
            const msg = json?.message || json?.error || raw || "Upsert cart failed";
            throw new Error(msg);
        }
        return json;
    } catch (error) {
        await handleApiError(error, {
            module: "other",
            action: "SpecialFeeTaxCloud_upsert_tax_cloud_cart",
            userId: userId || undefined,
            licenseType: licenseType || undefined,
            userName: userName || undefined,
            url: `${process.env.REACT_APP_API_URL}/taxcloud/carts/created-by/${supplierId}`,
            method: "POST",
            requestBody: payload
        }, token);
        throw error;
    }
}

// =========================
// High-level: Upsert just-in-time (Confirm & Pay or on shipping change)
// =========================
export async function upsertTaxBeforePayment(params: {
    token: string;
    userId: string;
    cartId?: string; // default: CART-<userId>
    items: CartItemInput[];
    destination: TaxCloudAddress; // from parent shipping address
    selectedShipping?: { id?: string; cost?: number } | null;
    selectedUiFee?: UiFee | null; // mapped UI fee (may include taxable)
    specialFeesRaw?: SpecialFee[]; // optional, if you want to double-check taxability by id
    originOverride?: TaxCloudAddress | null; // optional: pass origin to avoid extra fetch
}): Promise<{ response: any; taxTotal: number | null }> {
    const {
        token,
        userId,
        items,
        destination,
        selectedShipping,
        selectedUiFee,
        specialFeesRaw,
        originOverride,
    } = params;
    const cartId = params.cartId || `${userId}`;

    if (!token) throw new Error("Missing token");
    if (!userId) throw new Error("Missing userId");
    if (!destination) throw new Error("Missing destination address");
    // const origin = (await fetchDefaultWarehouseAddress(token));
    // if (!origin) throw new Error("Missing origin address (no default warehouse found)");

    const lines = buildTaxCloudLines(items, selectedShipping || null, selectedUiFee || null);

    // You can choose to always upsert; backend decides taxability.
    const response = await upsertTaxCloudCart({
        token,
        cartId,
        customerId: userId,
        // origin,
        destination,
        cartItems: lines,
    });

    // Try common response shapes for total tax
    const taxTotal = pickFirstNumber(response, [
        "taxTotal",
        "totalTax",
        "tax",
        "totals.tax",
    ]);

    return { response, taxTotal };
}

// =========================
// Small utility helpers
// =========================
function pickFirstNumber(obj: any, paths: string[]): number | null {
    if (!obj) return null;
    for (const p of paths) {
        const v = get(obj, p);
        const n = Number(v);
        if (!Number.isNaN(n)) return n;
    }
    return null;
}

function get(obj: any, path: string): any {
    try {
        return path.split(".").reduce((acc: any, key: string) => (acc == null ? undefined : acc[key]), obj);
    } catch {
        return undefined;
    }
}


// --- 1) Ping TaxCloud ------------------------------------------------
export async function pingTaxCloud(token: string): Promise<boolean> {
    const userId = getAuthCookie("userId");
    const licenseType = getAuthCookie("licenseType");
    const userName = getAuthCookie("userName");
    if(!supplierId) return false;
    try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/taxcloud/config/created-by/${supplierId}`, {
            method: "GET",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        safeConsole.log(res, "resok")
        return res.ok;
    } catch (error) {
        await handleApiError(error, {
            module: "other",
            action: "SpecialFeeTaxCloud_ping_tax_cloud",
            userId: userId || undefined,
            licenseType: licenseType || undefined,
            userName: userName || undefined,
            url: `${process.env.REACT_APP_API_URL}/taxcloud/config/created-by/${supplierId}`,
            method: "GET"
        }, token);
        return false;
    }
}

// --- 2) Build multiple special-fee lines (taxable-only) --------------
export function buildTaxableFeeLines(selectedFees: UiFee[]): CartLineInput[] {
    return (selectedFees || [])
        .filter(f => f && f.taxable && Number(f.amount) > 0)
        .map(f => ({
            itemId: f.title,
            price: Number(f.amount),
            quantity: 1,
            itemType: "special_fee" as const,
        }));
}

// --- 3) Upsert with product + shipping + many fee lines --------------
export async function upsertTaxCloudCartMulti(params: {
    token: string;
    userId: string;
    cartId?: string;
    destination: TaxCloudAddress;
    products: { sku: string; price: number; quantity: number }[];
    shipping?: { id?: string; cost?: number; title?: string } | null;
    taxableFees?: UiFee[];  // already filtered to the ones you selected; weâ€™ll auto-drop non-taxable here too
    originOverride?: TaxCloudAddress | null;
}) {
    const { token, userId, destination, products, shipping, taxableFees, originOverride,cartId } = params;
    safeConsole.log("upsertTaxCloudCartMulti", token, userId, destination, products, shipping, taxableFees, originOverride,cartId);
    // const origin = originOverride || (await fetchDefaultWarehouseAddress(token)) ;
    // if (!origin) throw new Error("No default warehouse address found");
    // if (!origin) return null;


    const productLines: CartLineInput[] = products.map(p => ({
        itemId: p.sku,
        price: Number(p.price || 0),
        quantity: Number(p.quantity || 1),
        itemType: "default",
    }));

    const shippingLine: CartLineInput[] =
        shipping && Number(shipping.cost) > 0
            ? [{ itemId:shipping.title || shipping.id || "SHIP001", price: Number(shipping.cost || 0), quantity: 1, itemType: "shipping" }]
            : [];

    const feeLines = buildTaxableFeeLines(taxableFees || []);

    return upsertTaxCloudCart({
        token,
        cartId: cartId || "",
        customerId: userId,
        // origin: originOverride || undefined,
        destination,
        cartItems: [...productLines, ...shippingLine, ...feeLines],
    });
}

// --- 4) Orders API (commit -> complete -> refund) --------------------
export async function createTaxCloudOrder(token: string, cartId: string, orderId: string) {
    const userId = getAuthCookie("userId");
    const licenseType = getAuthCookie("licenseType");
    const userName = getAuthCookie("userName");
    const payload = { cartId, orderId };
    try {
        // console.log(token, cartId, orderId,"this is ok")
        const res = await fetch(`${process.env.REACT_APP_API_URL}/taxcloud/orders/created-by/${supplierId}`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        });
        // console.log(res, "resokzsszszss")
        if (!res.ok) throw new Error("TaxCloud order creation failed");
        return res.json().catch(() => ({}));
    } catch (error) {
        await handleApiError(error, {
            module: "other",
            action: "SpecialFeeTaxCloud_create_tax_cloud_order",
            userId: userId || undefined,
            licenseType: licenseType || undefined,
            userName: userName || undefined,
            url: `${process.env.REACT_APP_API_URL}/taxcloud/orders/created-by/${supplierId}`,
            method: "POST",
            requestBody: payload
        }, token);
        throw error;
    }
}

export async function completeTaxCloudOrder(token: string, orderId: string, completedDateISO: string) {
    const userId = getAuthCookie("userId");
    const licenseType = getAuthCookie("licenseType");
    const userName = getAuthCookie("userName");
    const payload = { completedDate: completedDateISO };
    try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/taxcloud/orders/${orderId}/complete/created-by/${supplierId}`, {
            method: "PATCH",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error("TaxCloud order complete failed");
        return res.json().catch(() => ({}));
    } catch (error) {
        await handleApiError(error, {
            module: "other",
            action: "SpecialFeeTaxCloud_complete_tax_cloud_order",
            userId: userId || undefined,
            licenseType: licenseType || undefined,
            userName: userName || undefined,
            url: `${process.env.REACT_APP_API_URL}/taxcloud/orders/${orderId}/complete/created-by/${supplierId}`,
            method: "PATCH",
            requestBody: payload,
            additionalInfo: {
                orderId: orderId || null
            }
        }, token);
        throw error;
    }
}



export async function Refund(token: string, orderId: string): Promise<boolean> {
    const userId = getAuthCookie("userId");
    const licenseType = getAuthCookie("licenseType");
    const userName = getAuthCookie("userName");
    const payload = {};
    // console.log("refund called",token, orderId) 
    if (!token || !orderId) {
        safeConsole.warn("[TaxCloud] Refund skipped: missing token or order number");
        return false;
    }

    try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/taxcloud/orders/${orderId}/refund`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        });
        // console.log("[TaxCloud] refund response", res);
        return res.ok;
    } catch (error) {
        safeConsole.error("[TaxCloud] refund failed", error);
        await handleApiError(error, {
            module: "other",
            action: "SpecialFeeTaxCloud_refund",
            userId: userId || undefined,
            licenseType: licenseType || undefined,
            userName: userName || undefined,
            url: `${process.env.REACT_APP_API_URL}/taxcloud/orders/${orderId}/refund`,
            method: "POST",
            requestBody: payload,
            additionalInfo: {
                orderId: orderId || null
            }
        }, token);
        return false;
    }
}

export async function PostVersionOrdernumber(token: string, ordernumber: string,order_type: string): Promise<boolean> {
    const userId = getAuthCookie("userId");
    const licenseType = getAuthCookie("licenseType");
    const userName = getAuthCookie("userName");
    const payload = {order_type: order_type, order_number: ordernumber };
    if (!token || !ordernumber) {
        safeConsole.warn("[TaxCloud] Refund skipped: missing token or order number");
        return false;
    }

    try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/taxcloud_order/version`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        });
        // console.log("[TaxCloud] refund response", res);
        return res.ok;
    } catch (error) {
        safeConsole.error("[TaxCloud] refund failed", error);
        await handleApiError(error, {
            module: "other",
            action: "SpecialFeeTaxCloud_post_version_order_number",
            userId: userId || undefined,
            licenseType: licenseType || undefined,
            userName: userName || undefined,
            url: `${process.env.REACT_APP_API_URL}/taxcloud_order/version`,
            method: "POST",
            requestBody: payload,
            additionalInfo: {
                orderNumber: ordernumber || null,
                orderType: order_type || null
            }
        }, token);
        return false;
    }
}

export async function GetVersionOrdernumber(
    token: string,
    ordernumber: string,
    order_type: string
): Promise<any | null> {
    const userId = getAuthCookie("userId");
    const licenseType = getAuthCookie("licenseType");
    const userName = getAuthCookie("userName");
    if (!ordernumber) return null;
    try {
        const params = new URLSearchParams({
            order_type: order_type || "installation",
            order_number: ordernumber,
        });
        const res = await fetch(`${process.env.REACT_APP_API_URL}/taxcloud_order/version?${params.toString()}`, {
            method: "GET",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        if (!res.ok) return null;
        const data = await res.json().catch(() => null);
        return data;
    } catch (error) {
        await handleApiError(error, {
            module: "other",
            action: "SpecialFeeTaxCloud_get_version_order_number",
            userId: userId || undefined,
            licenseType: licenseType || undefined,
            userName: userName || undefined,
            url: `${process.env.REACT_APP_API_URL}/taxcloud_order/version?order_type=${order_type || "installation"}&order_number=${ordernumber}`,
            method: "GET",
            additionalInfo: {
                orderNumber: ordernumber || null,
                orderType: order_type || null
            }
        }, token);
        return null;
    }
}

export async function refundTaxCloudOrder(token: string, orderId: string) {
    const userId = getAuthCookie("userId");
    const licenseType = getAuthCookie("licenseType");
    const userName = getAuthCookie("userName");
    const payload = {};
    try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/taxcloud/orders/${orderId}/refund/created-by/${supplierId}`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error("TaxCloud refund failed");
        return res.json().catch(() => ({}));
    } catch (error) {
        await handleApiError(error, {
            module: "other",
            action: "SpecialFeeTaxCloud_refund_tax_cloud_order",
            userId: userId || undefined,
            licenseType: licenseType || undefined,
            userName: userName || undefined,
            url: `${process.env.REACT_APP_API_URL}/taxcloud/orders/${orderId}/refund/created-by/${supplierId}`,
            method: "POST",
            requestBody: payload,
            additionalInfo: {
                orderId: orderId || null
            }
        }, token);
        throw error;
    }
}



// --- UPS: types ----------------------------------------------------
export type UpsRateLine = {
  service_code: string;
  service_name: string;
  currency: string;
  transportation: string;
  total_charge: string;
  billing_weight: string;
  delivery_days: string;
  delivery_by_time: string;
};

export type UpsShipTo = {
  company_name?: string;
  is_residential?: boolean;
  address_line1: string;
  city: string;
  state: string;
  postal_code: string;
  country_code: string;
};

export type MappedShippingCost = {
  _id: string;          // service_code
  name: string;         // "UPS Ground"
  description: string;  // "3 business days â€¢ <time>"
  cost: number;         // total_charge
  is_free: boolean;
  is_taxable?: boolean;
  taxable?: boolean;
};

// --- UPS: fetch integration + shipper addresses --------------------
export async function fetchUpsIntegrations(token: string) {
    const userId = getAuthCookie("userId");
    const licenseType = getAuthCookie("licenseType");
    const userName = getAuthCookie("userName");
    const token1 = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJlbWFpbCI6ImJyeWFuQGV4YW1wbGUuY29tIiwiZXhwIjoxNzYwMTEzMTY5LCJpZCI6IjY4Yjc3YmQ2N2FkNjcyM2E5YzljNWFhNCIsImxpY2Vuc2VUeXBlIjoiU0lHTkFHRV9GSVJNIiwibmFtZSI6IkJyeWFuIEZhcm1lciIsIm9mZmljZXMiOm51bGwsInBob3RvIjoiIiwic3RhdHVzIjoiQWN0aXZlIiwidXNlclR5cGUiOiJzaWduYWdlX2Zpcm0iLCJ2aWQiOjB9.Un69MLjTkCIRioPWvJDNf7URdD3wARweeHLWuFSL-80"

    try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/shipping/ups/configs`, {
            headers: { Authorization: `Bearer ${token1}` }
        });
        // console.log(res,"fetchUpsIntegrations")
        if (!res.ok) return [];
        const data = await res.json().catch(() => []);
        return Array.isArray(data) ? data : [];
    } catch (error) {
        await handleApiError(error, {
            module: "other",
            action: "SpecialFeeTaxCloud_fetch_ups_integrations",
            userId: userId || undefined,
            licenseType: licenseType || undefined,
            userName: userName || undefined,
            url: `${process.env.REACT_APP_API_URL}/shipping/ups/configs`,
            method: "GET"
        }, token1);
        return [];
    }
}

export async function fetchUpsShipperAddresses(token: string) {
    const userId = getAuthCookie("userId");
    const licenseType = getAuthCookie("licenseType");
    const userName = getAuthCookie("userName");
    const token1 = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJlbWFpbCI6ImJyeWFuQGV4YW1wbGUuY29tIiwiZXhwIjoxNzYwMTEzMTY5LCJpZCI6IjY4Yjc3YmQ2N2FkNjcyM2E5YzljNWFhNCIsImxpY2Vuc2VUeXBlIjoiU0lHTkFHRV9GSVJNIiwibmFtZSI6IkJyeWFuIEZhcm1lciIsIm9mZmljZXMiOm51bGwsInBob3RvIjoiIiwic3RhdHVzIjoiQWN0aXZlIiwidXNlclR5cGUiOiJzaWduYWdlX2Zpcm0iLCJ2aWQiOjB9.Un69MLjTkCIRioPWvJDNf7URdD3wARweeHLWuFSL-80"

    try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/shipping/ups/shipper-addresses`, {
            headers: { Authorization: `Bearer ${token1}` }
        });
        // console.log(res,"fetchUpsShipperAddresses")
        if (!res.ok) return [];
        const data = await res.json().catch(() => []);
        return Array.isArray(data) ? data : [];
    } catch (error) {
        await handleApiError(error, {
            module: "other",
            action: "SpecialFeeTaxCloud_fetch_ups_shipper_addresses",
            userId: userId || undefined,
            licenseType: licenseType || undefined,
            userName: userName || undefined,
            url: `${process.env.REACT_APP_API_URL}/shipping/ups/shipper-addresses`,
            method: "GET"
        }, token1);
        return [];
    }
}

// --- UPS: call rates -----------------------------------------------
export async function fetchUpsRates(token: string, payload: {
  integration_id: string;
  shipper_address_id: string;
  ship_to: UpsShipTo;
  packages: Array<{ quantity?: number; length: number; width: number; height: number; weight: number; unit: "LBS"; }>;
}): Promise<UpsRateLine[]> {
    const userId = getAuthCookie("userId");
    const licenseType = getAuthCookie("licenseType");
    const userName = getAuthCookie("userName");
    const token1 = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJlbWFpbCI6ImJyeWFuQGV4YW1wbGUuY29tIiwiZXhwIjoxNzYwMTEzMTY5LCJpZCI6IjY4Yjc3YmQ2N2FkNjcyM2E5YzljNWFhNCIsImxpY2Vuc2VUeXBlIjoiU0lHTkFHRV9GSVJNIiwibmFtZSI6IkJyeWFuIEZhcm1lciIsIm9mZmljZXMiOm51bGwsInBob3RvIjoiIiwic3RhdHVzIjoiQWN0aXZlIiwidXNlclR5cGUiOiJzaWduYWdlX2Zpcm0iLCJ2aWQiOjB9.Un69MLjTkCIRioPWvJDNf7URdD3wARweeHLWuFSL-80"

    try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/shipping/ups/rates`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token1}`, "Content-Type": "application/json" },
            body: JSON.stringify({
                ...payload,
                packing_mode: "configured_boxes",
                ship_to: { ...payload.ship_to, ...upsDestinationDetails(payload.ship_to) },
                packages: payload.packages.map(pkg => ({ ...pkg, quantity: pkg.quantity ?? 1 })),
            }),
        });
        // console.log(res,"fetchUpsRates")
        if (!res.ok) return [];
        const data = await res.json().catch(() => []);
        return Array.isArray(data) ? data : [];
    } catch (error) {
        await handleApiError(error, {
            module: "other",
            action: "SpecialFeeTaxCloud_fetch_ups_rates",
            userId: userId || undefined,
            licenseType: licenseType || undefined,
            userName: userName || undefined,
            url: `${process.env.REACT_APP_API_URL}/shipping/ups/rates`,
            method: "POST",
            requestBody: payload
        }, token1);
        return [];
    }
}

// --- UPS: map to your list shape -----------------------------------
export function mapUpsRatesToShippingCosts(lines: UpsRateLine[]): MappedShippingCost[] {
  return (lines || []).map((ln) => ({
    _id: ln.service_code,
    name: `UPS ${ln.service_name}`,
    description: [
      ln.delivery_days ? `${ln.delivery_days} business days` : "",
      ln.delivery_by_time ? ln.delivery_by_time : ""
    ].filter(Boolean).join(" â€¢ "),
    cost: Number(ln.total_charge || ln.transportation || 0),
    is_free: false,
    is_taxable: true,
    taxable: true,
  }));
}

export function buildPackagesFromCart(cartItems: any[]) {
  const toLbs = (w: number, unit?: string) => {
    if (!Number.isFinite(w) || w <= 0) return 0;
    const u = String(unit || "").trim().toLowerCase();
    if (u === "g" || u === "gram" || u === "grams") return w / 453.59237;
    if (u === "kg" || u === "kgs" || u === "kilogram" || u === "kilograms") return w * 2.20462262;
    if (u === "oz" || u === "ounce" || u === "ounces") return w / 16;
    return w; // assume lbs
  };

  const pkgs: Array<{ length: number; width: number; height: number; weight: number; unit: "LBS" }> = [];

  (cartItems || []).forEach((row) => {
    // priority: row.dimensions -> row.package_dimensions -> variant.package_dimensions -> product.package_dimensions
    const variant =
      Array.isArray(row.product?.variants)
        ? row.product!.variants!.find((v: { variant_id: any; }) => v.variant_id === (row as any).variant_id) // if you store variant_id on row
        : undefined;

    const dims =
      row.dimensions ||
      row.package_dimensions ||
      variant?.package_dimensions ||
      row.product?.package_dimensions;

    if (!dims) return;

    const L = Number(dims.length);
    const W = Number(dims.width);
    const H = Number(dims.height);
    const wLbs = toLbs(Number(dims.weight), dims.weight_type);

    if (!(L > 0 && W > 0 && H > 0 && wLbs > 0)) return;

    const qty = Math.max(1, Number((row as any).quantity || 1));
    for (let i = 0; i < qty; i++) {
      pkgs.push({ length: L, width: W, height: H, weight: wLbs, unit: "LBS" });
    }
  });

  return pkgs; // [] if none had valid dimensions
}



// --- UPS: orchestrator used by your screen -------------------------
export async function loadUpsLiveRates(
  token: string,
  params: { shipTo: UpsShipTo; cartItems: any[] }
): Promise<MappedShippingCost[]> {
                const token1 = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJlbWFpbCI6ImJyeWFuQGV4YW1wbGUuY29tIiwiZXhwIjoxNzYwMTEzMTY5LCJpZCI6IjY4Yjc3YmQ2N2FkNjcyM2E5YzljNWFhNCIsImxpY2Vuc2VUeXBlIjoiU0lHTkFHRV9GSVJNIiwibmFtZSI6IkJyeWFuIEZhcm1lciIsIm9mZmljZXMiOm51bGwsInBob3RvIjoiIiwic3RhdHVzIjoiQWN0aXZlIiwidXNlclR5cGUiOiJzaWduYWdlX2Zpcm0iLCJ2aWQiOjB9.Un69MLjTkCIRioPWvJDNf7URdD3wARweeHLWuFSL-80"

  const [integs, shipperAddrs] = await Promise.all([
    fetchUpsIntegrations(token1),
    fetchUpsShipperAddresses(token1),
  ]);

  const integration_id = String(integs?.[0]?.id || integs?.[0]?._id || integs?.[0]?.integration_id || "");
  // console.log(integration_id,"idintigrations")
  const shipper_address_id = String(
    shipperAddrs?.[0]?.shipper_address_id || shipperAddrs?.[0]?._id || shipperAddrs?.[0]?.id || ""
  );
//  console.log(shipper_address_id,"shipper_address_id")
  if (!integration_id || !shipper_address_id) return [];

  const packages = buildPackagesFromCart(params.cartItems);
  const lines = await fetchUpsRates(token1   , {
    integration_id,
    shipper_address_id,
    ship_to: params.shipTo,
    packages,
  });

  return mapUpsRatesToShippingCosts(lines);
}



// Slaesortds and removal and installations code was below i was implemented by me


/* ===============================
 * TaxCloud v2 helpers (add-only)
 * =============================== */

// Safe non-empty id for fee lines (your API rejects empty itemId)
export const TAXCLOUD_SPECIAL_FEE_ITEM_ID_V2 = "Special Fee";

// Lightweight address type for these helpers (keeps old types intact)
export type TaxCloudAddressV2 = {
  addressLine1: string;
  city: string;
  state: string; // 2-letter preferred
  zip: string;
};

type CartLineV2 = {
  itemId: string;
  price: number;
  quantity: number;
  itemType: "default" | "shipping" | "special_fee";
};

type UiFeeV2 = {
  id?: string;
  amount: number;
  taxable?: boolean;
};

/**
 * Fetch default warehouse origin (reuses your existing implementation if exported).
 * If you already have fetchDefaultWarehouseAddress, weâ€™ll call it.
 */
async function _getOriginV2(token: string): Promise<TaxCloudAddressV2> {
    // console.log("fetching default warehouse address for origin");
  // @ts-ignore â€“ prefer existing export if present
  if (typeof fetchDefaultWarehouseAddress === "function") {
    // @ts-ignore
    const o = await fetchDefaultWarehouseAddress(token);
    if (o && o.addressLine1) return o as TaxCloudAddressV2;
  }
  // throw new Error("No default warehouse address found so refresh and try again, or pass originOverride to bypass this lookup");
//   console.log("fetching Failed default Taxcloud address for origin");
  return null as any;
}

/**
 * Core upsert (v2): posts to created-by/{supplierId} with optional cartNumber
 */
export async function taxcloudUpsertCart_v2(params: {
  token: string;
  supplierId: string;              // goes in URL
  customerId?: string;             // defaults to supplierId
  destination: TaxCloudAddressV2;
  cartItems: CartLineV2[];
  orderNumber?: string;            // sent as cartNumber
  originOverride?: TaxCloudAddressV2 | null;
}) {
  const {
    token,
    supplierId,
    destination,
    cartItems,
    orderNumber,
    originOverride,
  } = params;

  const userId = getAuthCookie("userId");
  const licenseType = getAuthCookie("licenseType");
  const userName = getAuthCookie("userName");

  const customerId = params.customerId || supplierId;
//   const origin = originOverride || (await _getOriginV2(token));
//  console.log("taxcloudUpsertCart_v2", token, supplierId, customerId, destination, cartItems, orderNumber, originOverride);
  const payload: any = {
    customerId,
    // origin,
    destination,
    cartItems,
  };
  if (orderNumber && typeof orderNumber === "string") {
    payload.cartId = String(orderNumber); // must be a string
  }

  try {
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/taxcloud/carts/created-by/${supplierId}`,
      {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }
    );

    const text = await res.text();
    let json: any = null;
    try { json = text ? JSON.parse(text) : null; } catch {}
    if (!res.ok) throw new Error(json?.message || json?.error || text || "Upsert cart failed");
    return json ?? {};
  } catch (error) {
    await handleApiError(error, {
      module: "other",
      action: "SpecialFeeTaxCloud_taxcloud_upsert_cart_v2",
      userId: userId || undefined,
      licenseType: licenseType || undefined,
      userName: userName || undefined,
      url: `${process.env.REACT_APP_API_URL}/taxcloud/carts/created-by/${supplierId}`,
      method: "POST",
      requestBody: payload
    }, token);
    throw error;
  }
}

/**
 * SALES: products + shipping + taxable fees
 */
export async function taxcloudUpsertForSales_v2(params: {
  token: string;
  supplierId: string;              // cartId in URL
  destination: TaxCloudAddressV2;
  products: { sku: string; price: number; quantity: number }[];
  shipping?: { id?: string; cost?: number } | null;
  fees?: UiFeeV2[];                // pass only selected fees
  orderNumber?: string;
  originOverride?: TaxCloudAddressV2 | null;
}) {
  const { token, supplierId, destination, products, shipping, fees, orderNumber, originOverride } = params;
// console.log("taxcloudUpsertForSales_v2", token, supplierId, destination, products, shipping, fees, orderNumber, originOverride);
  const productLines: CartLineV2[] = (products || []).map((p) => ({
    itemId: String(p.sku || "SKU"),
    price: Number(p.price || 0),
    quantity: Number(p.quantity || 1),
    itemType: "default",
  }));

  const shippingLine: CartLineV2[] =
    shipping && Number(shipping.cost) > 0
      ? [{
          itemId: shipping.id || "SHIP001",
          price: Number(shipping.cost || 0),
          quantity: 1,
          itemType: "shipping",
        }]
      : [];

  const feeLines: CartLineV2[] = (fees || [])
    .filter((f) => f && f.taxable !== false && Number(f.amount) > 0)
    .map((f) => ({
      itemId: f.id && f.id.trim() ? f.id : TAXCLOUD_SPECIAL_FEE_ITEM_ID_V2,
      price: Number(f.amount),
      quantity: 1,
      itemType: "special_fee",
    }));

  return taxcloudUpsertCart_v2({
    token,
    supplierId,
    customerId: supplierId,
    destination,
    cartItems: [...productLines, ...shippingLine, ...feeLines],
    orderNumber,
    originOverride: originOverride || null,
  });
}

/**
 * INSTALL / REMOVAL: single synthetic product line (installation_cost),
 * itemId = first non-empty sku from line_items, + one fee line with non-empty id.
 */
export async function taxcloudUpsertForInstallRemoval_v2(params: {
  token: string;
  supplierId: string;              // cartId in URL
  destination: TaxCloudAddressV2;
  installationCost: number;        // order.installation_cost
  specialFeeAmount?: number;       // order.special_fee_amount
  productSkus?: string[];          // to choose product itemId
  orderNumber?: string;
  originOverride?: TaxCloudAddressV2 | null;
}) {
  const {
    token, supplierId, destination, installationCost,
    specialFeeAmount = 0, productSkus = [], orderNumber, originOverride,
  } = params;

  const lines: CartLineV2[] = [];

  const productItemId =
    productSkus.find((s) => typeof s === "string" && s.trim().length > 0) || "INSTALLATION_COST";

  if (Number(installationCost) > 0) {
    lines.push({
      itemId: productItemId,
      price: Number(installationCost),
      quantity: 1,
      itemType: "default",
    });
  }

  if (Number(specialFeeAmount) > 0) {
    lines.push({
      itemId: TAXCLOUD_SPECIAL_FEE_ITEM_ID_V2, // non-empty id required by API
      price: Number(specialFeeAmount),
      quantity: 1,
      itemType: "special_fee",
    });
  }

  return taxcloudUpsertCart_v2({
    token,
    supplierId,
    customerId: supplierId,
    destination,
    cartItems: lines,
    orderNumber,
    originOverride: originOverride || null,
  });
}

/**
 * REPORT (commit): must send TaxCloud cartId returned by carts API.
 * Optionally include your human-readable orderNumber.
 */
export async function taxcloudCreateOrder_v2(
  token: string,
  supplierId: string,      // URL path created-by/{supplierId}
  taxcloudCartId: string,  // resp.items[0].cartId from upsert
  orderNumber?: string
) {
  const userId = getAuthCookie("userId");
  const licenseType = getAuthCookie("licenseType");
  const userName = getAuthCookie("userName");
  const body: any = { cartId: String(taxcloudCartId) };
  if (orderNumber) body.orderId = String(orderNumber);
  //   console.log(supplierId, orderNumber,body.orderId, "supplierIdtaxcloudCreateOrder_v2")
  // console.log(body, "taxcloudCreateOrder_v2")
  try {
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/taxcloud/orders/created-by/${supplierId}`,
      {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }
    );

    const text = await res.text();
    let json: any = null;
    try { json = text ? JSON.parse(text) : null; } catch {}
    if (!res.ok) throw new Error(json?.detail || json?.message || text || "TaxCloud order creation failed");
    return json ?? {};
  } catch (error) {
    await handleApiError(error, {
      module: "other",
      action: "SpecialFeeTaxCloud_taxcloud_create_order_v2",
      userId: userId || undefined,
      licenseType: licenseType || undefined,
      userName: userName || undefined,
      url: `${process.env.REACT_APP_API_URL}/taxcloud/orders/created-by/${supplierId}`,
      method: "POST",
      requestBody: body
    }, token);
    throw error;
  }
}

/** optional: re-export your ping to keep naming consistent */
export async function taxcloudPing_v2(token: string): Promise<boolean> {
  const userId = getAuthCookie("userId");
  const licenseType = getAuthCookie("licenseType");
  const userName = getAuthCookie("userName");
  try {
    // @ts-ignore â€" reuse your existing ping if available
    if (typeof pingTaxCloud === "function") return await pingTaxCloud(token);
  } catch {}
  try {
    const supplierId = localStorage.getItem("supplierId") || getAuthCookie("userId") || "";
    const res = await fetch(`${process.env.REACT_APP_API_URL}/taxcloud/config/created-by/${supplierId}`, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    });
    return res.ok;
  } catch (error) {
    const supplierId = localStorage.getItem("supplierId") || getAuthCookie("userId") || "";
    await handleApiError(error, {
      module: "other",
      action: "SpecialFeeTaxCloud_taxcloud_ping_v2",
      userId: userId || undefined,
      licenseType: licenseType || undefined,
      userName: userName || undefined,
      url: `${process.env.REACT_APP_API_URL}/taxcloud/config/created-by/${supplierId}`,
      method: "GET"
    }, token);
    return false;
  }
}


// âœ… Add (or replace) this implementation
export async function completeTaxCloudOrderV2(
  token: string,
  supplierId: string,
  orderId: string,
  completedDateISO?: string
) {
  const userId = getAuthCookie("userId");
  const licenseType = getAuthCookie("licenseType");
  const userName = getAuthCookie("userName");
  const url = `${process.env.REACT_APP_API_URL}/taxcloud/orders/${encodeURIComponent(
    orderId
  )}/complete/created-by/${encodeURIComponent(supplierId)}`;
  const payload = { completedDate: completedDateISO || new Date().toISOString() };

  try {
    const res = await fetch(url, {
      method: "PATCH",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const text = await res.text();
    let json: any = null;
    try { json = text ? JSON.parse(text) : null; } catch {}
    if (!res.ok) {
      throw new Error(json?.detail || json?.message || text || "TaxCloud order complete failed");
    }
    return json ?? {};
  } catch (error) {
    await handleApiError(error, {
      module: "other",
      action: "SpecialFeeTaxCloud_complete_tax_cloud_order_v2",
      userId: userId || undefined,
      licenseType: licenseType || undefined,
      userName: userName || undefined,
      url: url,
      method: "PATCH",
      requestBody: payload,
      additionalInfo: {
        orderId: orderId || null
      }
    }, token);
    throw error;
  }
}


// specialfee data get useing below calls


// --- User-based Special Fee fetcher ------------------------------------
// pass the final selected person id (office / agent / office-agent) as userId
export async function fetchUserbaseSpeicalfee(token: string, userId: string) {
  const licenseType = getAuthCookie("licenseType");
  const userName = getAuthCookie("userName");
  // NOTE: per your request this endpoint uses a firm token; keeping as-is.
  const token1 =
    "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJlbWFpbCI6ImJyeWFuQGV4YW1wbGUuY29tIiwiZXhwIjoxNzYwMTEzMTY5LCJpZCI6IjY4Yjc3YmQ2N2FkNjcyM2E5YzljNWFhNCIsImxpY2Vuc2VUeXBlIjoiU0lHTkFHRV9GSVJNIiwibmFtZSI6IkJyeWFuIEZhcm1lciIsIm9mZmljZXMiOm51bGwsInBob3RvIjoiIiwic3RhdHVzIjoiQWN0aXZlIiwidXNlclR5cGUiOiJzaWduYWdlX2Zpcm0iLCJ2aWQiOjB9.Un69MLjTkCIRioPWvJDNf7URdD3wARweeHLWuFSL-80";

  if (!userId) return [];
  try {
    const res = await fetch(`${process.env.REACT_APP_API_URL}/special-fees/by-user/${userId}`, {
      method: "GET",
      headers: { Authorization: `Bearer ${token1}`, "Content-Type": "application/json" },
    });
    if (!res.ok) return [];
    const data = await res.json().catch(() => []);
    return Array.isArray(data) ? data : [];
  } catch (error) {
    await handleApiError(error, {
      module: "other",
      action: "SpecialFeeTaxCloud_fetch_userbase_special_fee",
      userId: userId || undefined,
      licenseType: licenseType || undefined,
      userName: userName || undefined,
      url: `${process.env.REACT_APP_API_URL}/special-fees/by-user/${userId}`,
      method: "GET"
    }, token1);
    return [];
  }
}

// friendly alias (correct spelling)
export const fetchUserbaseSpecialFee = fetchUserbaseSpeicalfee;


// 
// removal and installtion manually tax calculation

export async function pingTaxCloudinstalltionandremovals(token: string) {
  const userId = getAuthCookie("userId");
  const licenseType = getAuthCookie("licenseType");
  const userName = getAuthCookie("userName");
  try {
    const res = await fetch(`${process.env.REACT_APP_API_URL}/taxcloud/ping`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.ok;
  } catch (error) {
    await handleApiError(error, {
      module: "other",
      action: "SpecialFeeTaxCloud_ping_tax_cloud_installation_and_removals",
      userId: userId || undefined,
      licenseType: licenseType || undefined,
      userName: userName || undefined,
      url: `${process.env.REACT_APP_API_URL}/taxcloud/ping`,
      method: "GET"
    }, token);
    return false;
  }
}

/** manual tax when taxcloud is unavailable, respecting is_taxable flag */
export function calcManualRemovalTaxes(params: {
  removalCost: number;                 // base
  specialFeeBreakdown: { amount: number; taxable?: boolean }[];
  isTaxable: boolean;
  ratePct: number;                     // e.g. 6
}) {
  const rate = Number(params.ratePct || 0) / 100;
  const base = Number(params.removalCost || 0);

  const productTax = params.isTaxable ? base * rate : 0;

  const feeTaxablePart = params.specialFeeBreakdown
    .filter(f => !!f.taxable)
    .reduce((n, f) => n + Number(f.amount || 0), 0);
  const specialFeeTax = params.isTaxable ? feeTaxablePart * rate : 0;

  const totalTax = productTax + specialFeeTax;
  const specialFeeTaxPct = params.isTaxable ? Number(params.ratePct || 0) : 0;

  return {
    productTaxAmount: Number(productTax.toFixed(2)),
    specialFeeTaxAmount: Number(specialFeeTax.toFixed(2)),
    totalTaxAmount: Number(totalTax.toFixed(2)),
    productTaxPercentage: params.isTaxable ? Number(params.ratePct || 0) : 0,
    specialFeeTaxPercentage: specialFeeTaxPct,
  };
}
























































// // specialFeeTaxUtils.ts
// // All helper types & functions in one place, ready to import into your checkout screens.
// // No React imports; pure TypeScript utilities.

// // =========================
// // Types
// // =========================
// export type SpecialFee = {
//     id: string;
//     name: string;
//     amount: number;
//     taxable: boolean;
//     available_on: string[]; // e.g. ["checkoutPage"]
//     required: boolean;
//     status: boolean;
//     limit_to_agents: string[]; // userIds
//     limit_to_brokerages: string[]; // brokerageIds (can be same id domain as user)
//     limit_to_groups: string[]; // group ids
// };

// export type CustomerGroup = {
//     _id?: string;
//     id?: string;
//     name?: string;
//     // optional, in case your API includes members inline
//     members?: Array<string | { id?: string; _id?: string }>;
//     users?: Array<string | { id?: string; _id?: string }>;
// };

// export type UiFee = {
//     id: string;
//     title: string;
//     subtitle?: string;
//     amount: number;
//     required?:boolean;
//     note?: string;
//     icon?: string;
//     // extra fields (safe for TS structural typing; UI can ignore them)
//     taxable?: boolean;
//     noteTitle?: string;
//     noteText?: string;
// };

// export type CartItemInput = {
//     sku: string; // itemId in TaxCloud
//     price: number;
//     quantity: number;
// };

// export type TaxCloudAddress = {
//     addressLine1: string;
//     city: string;
//     state: string; // 2-letter code preferred
//     zip: string;
// };

// export type CartLineInput = {
//     itemId: string;
//     price: number;
//     quantity: number;
//     title?: string;  
//     sku?: string;
//     shipping_name?: string;
//     itemType: "default" | "shipping" | "special_fee";
// };

// const supplierId = localStorage.getItem("supplierId") ;
// const warehouseuserId = getAuthCookie("userId");
// // =========================
// // Fetchers (plain; call these from your components)
// // =========================
// export async function fetchSpecialFees(token: string): Promise<SpecialFee[]> {
//     const res = await fetch(`${process.env.REACT_APP_API_URL}/special-fees/created-by/${supplierId}`, {
//         method: "GET",
//         headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
//     });
//     // console.log(res,"special-fee dataa")
//     if (!res.ok) throw new Error("Failed to fetch special fees");
//     const data = await res.json();
//     return Array.isArray(data) ? (data as SpecialFee[]) : [];
// }

// export async function fetchGroups(token: string): Promise<CustomerGroup[]> {
//     // If your API is actually /customer-groups, just switch the path here.
//     const res = await fetch(`${process.env.REACT_APP_API_URL}/groups`, {
//         method: "GET",
//         headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
//     });
//     if (!res.ok) throw new Error("Failed to fetch groups");
//     const data = await res.json();
//     return Array.isArray(data) ? (data as CustomerGroup[]) : [];
// }

// // =========================
// // Group membership checks (two strategies)
// // =========================
// export async function isUserInGroupViaApi(
//     token: string,
//     groupId: string,
//     userId: string
// ): Promise<boolean> {
//     try {
//         // Adjust if your backend exposes a different route
//         const res = await fetch(
//             `${process.env.REACT_APP_API_URL}/customer-groups/${groupId}/users/${userId}/exists`,
//             { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
//         );
//         if (!res.ok) return false;
//         const data = await res.json();
//         return !!(data && (data.exists || data.member === true));
//     } catch {
//         return false;
//     }
// }

// export function isUserInGroupFromList(
//     groups: CustomerGroup[] | undefined,
//     groupId: string,
//     userId: string
// ): boolean {
//     if (!groups || !groupId || !userId) return false;
//     const g = groups.find((x) => (x._id || x.id) === groupId);
//     if (!g) return false;
//     const list = Array.isArray(g.members)
//         ? g.members
//         : Array.isArray(g.users)
//             ? g.users
//             : [];
//     return list.some((m: any) => {
//         if (typeof m === "string") return m === userId;
//         const mid = m?.id || m?._id;
//         return mid === userId;
//     });
// }

// // =========================
// // Eligibility & mapping
// // =========================
// export async function pickEligibleSpecialFee(
//     fees: SpecialFee[],
//     userId: string,
//     token: string,
//     options?: {
//         groups?: CustomerGroup[];
//         checkUserInGroup?: (groupId: string) => Promise<boolean>; // custom checker override
//         requireAvailableOn?: string; // e.g., "checkoutPage"
//     }
// ): Promise<SpecialFee | null> {
//     if (!userId || !Array.isArray(fees) || fees.length === 0) return null;

//     const targetPage = options?.requireAvailableOn || "checkoutPage";
//     const list = fees.filter((f) => (f.available_on || []).includes(targetPage));
//     for (const fee of list) {
//         // Direct matches
//         if (Array.isArray(fee.limit_to_agents) && fee.limit_to_agents.includes(userId)) return fee;
//         if (Array.isArray(fee.limit_to_brokerages) && fee.limit_to_brokerages.includes(userId)) return fee;

//         // Group matches
//         if (Array.isArray(fee.limit_to_groups) && fee.limit_to_groups.length) {
//             for (const gid of fee.limit_to_groups) {
//                 let inGroup = false;
//                 if (options?.checkUserInGroup) inGroup = await options.checkUserInGroup(gid);
//                 else inGroup = isUserInGroupFromList(options?.groups || [], gid, userId);
//                 if (inGroup) return fee;
//             }
//         }
//     }
//     return null;
// }

// export function mapSpecialFeeToUiFee(fee: SpecialFee): UiFee {
//     return {
//         id: fee.id || "special",
//         title: fee.name,
//         subtitle: fee.required ? "(Required)" : "(Optional)",
//         amount: Number(fee.amount || 0),
//         note: fee.required
//             ? "This fee is required for this order."
//             : "You can include this optional fee.",
//         icon: "rocket",
//         taxable: !!fee.taxable,
//         noteTitle: fee.required ? "Required Fee" : "Optional Fee",
//         noteText: fee.taxable ? "Tax will also be calculated for this fee." : "No tax on this fee.",
//     };
// }

// // =========================
// // TaxCloud: build lines & addresses
// // =========================
// export function buildTaxCloudLines(
//     items: CartItemInput[],
//     selectedShipping?: { id?: string; cost?: number,title?: string } | null,
//     selectedFee?: UiFee | null
// ): CartLineInput[] {
//     const lines: CartLineInput[] = [];

//     // 1) Product lines
//     for (const it of items || []) {
//         lines.push({
//             itemId: String(it.sku || "SKU"),
//             price: Number(it.price || 0),
//             quantity: Number(it.quantity || 1),
//             itemType: "default",
//         });
//     }

//     // 2) Shipping line (manual selection)
//     if (selectedShipping && Number(selectedShipping.cost) > 0) {
//         lines.push({
//             itemId: selectedShipping.title || "Shipping",
//             price: Number(selectedShipping.cost || 0),
//             quantity: 1,
//             itemType: "shipping",
//         });
//     }

//     // 3) Special fee line
//     if (selectedFee && Number(selectedFee.amount) > 0) {
//         lines.push({
//             itemId: selectedFee.title || "SPECIAL",
//             price: Number(selectedFee.amount || 0),
//             quantity: 1,
//             itemType: "special_fee",
//         });
//     }

//     return lines;
// }

// export function toTaxCloudAddressFromObject(obj: any): TaxCloudAddress | null {
//     if (!obj) return null;
//     // try common shapes
//     const a = obj.address_details || obj.address || obj || obj.shippingAddress;
//     // console.log("toTaxCloudAddressFromObject", a);
//     // if (!a) return null;
//     const addressLine1 = a.addressLine1 || a.line1 || a.address1 || "";
//     const city = a.city || a.cityCode || "";
//     const state = a.stateCode || "";
//     const zip = a.zip || a.postalCode || a.postcode || "";
//     // console.log(addressLine1, city, state, zip, "toTaxCloudAddressFromObject12233");
//     // if (!addressLine1 || !city || !state || !zip) return null;
//     return { addressLine1, city, state, zip };
// }

// function addressFromWarehouseRow(row: any): TaxCloudAddress | null {
//     const a = row?.address;
//     if (!a) return null;
//     return {
//         addressLine1: a.addressLine1 || "",
//         city: a.city || a.cityCode || "",
//         state: a.stateCode || a.state || "",
//         zip: a.zip || "",
//     };
// }

// export async function fetchDefaultWarehouseAddress(token: string): Promise<TaxCloudAddress | null> {
//     try {

//         const res = await fetch(`${process.env.REACT_APP_API_URL}/warehouses/datatable/created-by/${supplierId || warehouseuserId}`, {
//             method: "POST",
//             headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
//             body: JSON.stringify({
//                 page: 1,
//                 limit: 50,
//                 search: "",
//                 filters: {},
//                 sort: { field: "created_at", order: "desc" },
//                 fields: ["label", "address", "status", "created_at", "is_default"],
//             }),
//         });
//         const data = await res.json();
//         const rows: any[] = Array.isArray(data?.data) ? data.data : [];
//         const def = rows.find((r) => r?.is_default === true) || rows[0];
//         return def ? addressFromWarehouseRow(def) : null;
//     } catch {
//         return null;
//     }
// }

// // =========================
// // TaxCloud: upsert cart
// // =========================
// export async function upsertTaxCloudCart(params: {
//     token: string;
//     cartId: string;
//     customerId: string;
//     origin: TaxCloudAddress;
//     destination: TaxCloudAddress;
//     cartItems: CartLineInput[];
// }): Promise<any> {
//     const { token, cartId, customerId, origin, destination, cartItems } = params;
//     console.log("upsertTaxCloudCarcheckert", cartId, customerId, origin, destination, cartItems);  
//     const payload = { cartId, customerId, origin, destination, cartItems };
//     if(!supplierId) return null;
//     const res = await fetch(`${process.env.REACT_APP_API_URL}/taxcloud/carts/created-by/${supplierId}`, {
//         method: "POST",
//         headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
//         body: JSON.stringify(payload),
//     });

//     const raw = await res.text();
//     let json: any = null;
//     try {
//         json = raw ? JSON.parse(raw) : null;
//     } catch {
//         json = null;
//     }

//     if (!res.ok) {
//         const msg = json?.message || json?.error || raw || "Upsert cart failed";
//         throw new Error(msg);
//     }
//     return json;
// }

// // =========================
// // High-level: Upsert just-in-time (Confirm & Pay or on shipping change)
// // =========================
// export async function upsertTaxBeforePayment(params: {
//     token: string;
//     userId: string;
//     cartId?: string; // default: CART-<userId>
//     items: CartItemInput[];
//     destination: TaxCloudAddress; // from parent shipping address
//     selectedShipping?: { id?: string; cost?: number } | null;
//     selectedUiFee?: UiFee | null; // mapped UI fee (may include taxable)
//     specialFeesRaw?: SpecialFee[]; // optional, if you want to double-check taxability by id
//     originOverride?: TaxCloudAddress | null; // optional: pass origin to avoid extra fetch
// }): Promise<{ response: any; taxTotal: number | null }> {
//     const {
//         token,
//         userId,
//         items,
//         destination,
//         selectedShipping,
//         selectedUiFee,
//         specialFeesRaw,
//         originOverride,
//     } = params;
//     const cartId = params.cartId || `${userId}`;

//     if (!token) throw new Error("Missing token");
//     if (!userId) throw new Error("Missing userId");
//     if (!destination) throw new Error("Missing destination address");
//     const origin = (await fetchDefaultWarehouseAddress(token));
//     if (!origin) throw new Error("Missing origin address (no default warehouse found)");

//     const lines = buildTaxCloudLines(items, selectedShipping || null, selectedUiFee || null);

//     // You can choose to always upsert; backend decides taxability.
//     const response = await upsertTaxCloudCart({
//         token,
//         cartId,
//         customerId: userId,
//         origin,
//         destination,
//         cartItems: lines,
//     });

//     // Try common response shapes for total tax
//     const taxTotal = pickFirstNumber(response, [
//         "taxTotal",
//         "totalTax",
//         "tax",
//         "totals.tax",
//     ]);

//     return { response, taxTotal };
// }

// // =========================
// // Small utility helpers
// // =========================
// function pickFirstNumber(obj: any, paths: string[]): number | null {
//     if (!obj) return null;
//     for (const p of paths) {
//         const v = get(obj, p);
//         const n = Number(v);
//         if (!Number.isNaN(n)) return n;
//     }
//     return null;
// }

// function get(obj: any, path: string): any {
//     try {
//         return path.split(".").reduce((acc: any, key: string) => (acc == null ? undefined : acc[key]), obj);
//     } catch {
//         return undefined;
//     }
// }


// // --- 1) Ping TaxCloud ------------------------------------------------
// export async function pingTaxCloud(token: string): Promise<boolean> {
//   if(!supplierId) return false;
//     try {
//         const res = await fetch(`${process.env.REACT_APP_API_URL}/taxcloud/config/created-by/${supplierId}`, {
//             method: "GET",
//             headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
//         });
//         console.log(res, "resok")
//         return res.ok;
//     } catch {
//         return false;
//     }
// }

// // --- 2) Build multiple special-fee lines (taxable-only) --------------
// export function buildTaxableFeeLines(selectedFees: UiFee[]): CartLineInput[] {
//     return (selectedFees || [])
//         .filter(f => f && f.taxable && Number(f.amount) > 0)
//         .map(f => ({
//             itemId: f.title,
//             price: Number(f.amount),
//             quantity: 1,
//             itemType: "special_fee" as const,
//         }));
// }

// // --- 3) Upsert with product + shipping + many fee lines --------------
// export async function upsertTaxCloudCartMulti(params: {
//     token: string;
//     userId: string;
//     cartId?: string;
//     supplierId?: string;
//     destination: TaxCloudAddress;
//     products: { sku: string; price: number; quantity: number }[];
//     shipping?: { id?: string; cost?: number; title?: string } | null;
//     taxableFees?: UiFee[];  // already filtered to the ones you selected; we’ll auto-drop non-taxable here too
//     originOverride?: TaxCloudAddress | null;
// }) {
//     const { token, userId, destination, products, shipping, taxableFees, originOverride,cartId } = params;
//     console.log("11111111111111",params)
//     const origin = originOverride || (await fetchDefaultWarehouseAddress(token));
//     if (!origin) throw new Error("No default warehouse address found");



//     const productLines: CartLineInput[] = products.map(p => ({
//         itemId: p.sku,
//         price: Number(p.price || 0),
//         quantity: Number(p.quantity || 1),
//         itemType: "default",
//     }));

//     const shippingLine: CartLineInput[] =
//         shipping && Number(shipping.cost) > 0
//             ? [{ itemId:shipping.title || shipping.id || "SHIP001", price: Number(shipping.cost || 0), quantity: 1, itemType: "shipping" }]
//             : [];

//     const feeLines = buildTaxableFeeLines(taxableFees || []);

//     return upsertTaxCloudCart({
//         token,
//         cartId: userId || "",
//         customerId: userId,
//         origin,
//         destination,
//         cartItems: [...productLines, ...shippingLine, ...feeLines],
//     });
// }

// // --- 4) Orders API (commit -> complete -> refund) --------------------
// export async function createTaxCloudOrder(token: string, cartId: string, orderId: string) {
//     // console.log(token, cartId, orderId,"this is ok")
//     const res = await fetch(`${process.env.REACT_APP_API_URL}/taxcloud/orders/created-by/${supplierId}`, {
//         method: "POST",
//         headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
//         body: JSON.stringify({ cartId, orderId }),
//     });
//     // console.log(res, "resokzsszszss")
//     if (!res.ok) throw new Error("TaxCloud order creation failed");
//     return res.json().catch(() => ({}));
// }

// export async function completeTaxCloudOrder(token: string, orderId: string, completedDateISO: string) {
//     const res = await fetch(`${process.env.REACT_APP_API_URL}/taxcloud/orders/${orderId}/complete/created-by/${supplierId}`, {
//         method: "PATCH",
//         headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
//         body: JSON.stringify({ completedDate: completedDateISO }),
//     });
//     if (!res.ok) throw new Error("TaxCloud order complete failed");
//     return res.json().catch(() => ({}));
// }

// export async function refundTaxCloudOrder(token: string, orderId: string) {
//     const res = await fetch(`${process.env.REACT_APP_API_URL}/taxcloud/orders/${orderId}/refund/created-by/${supplierId}`, {
//         method: "POST",
//         headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
//         body: JSON.stringify({}),
//     });
//     if (!res.ok) throw new Error("TaxCloud refund failed");
//     return res.json().catch(() => ({}));
// }



// // --- UPS: types ----------------------------------------------------
// export type UpsRateLine = {
//   service_code: string;
//   service_name: string;
//   currency: string;
//   transportation: string;
//   total_charge: string;
//   billing_weight: string;
//   delivery_days: string;
//   delivery_by_time: string;
// };

// export type UpsShipTo = {
//   address_line1: string;
//   city: string;
//   state: string;
//   postal_code: string;
//   country_code: string;
// };

// export type MappedShippingCost = {
//   _id: string;          // service_code
//   name: string;         // "UPS Ground"
//   description: string;  // "3 business days • <time>"
//   cost: number;         // total_charge
//   is_free: boolean;
//   is_taxable?: boolean;
//   taxable?: boolean;
// };

// // --- UPS: fetch integration + shipper addresses --------------------
// export async function fetchUpsIntegrations(token: string) {
//             const token1 = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJlbWFpbCI6ImJyeWFuQGV4YW1wbGUuY29tIiwiZXhwIjoxNzYwMTEzMTY5LCJpZCI6IjY4Yjc3YmQ2N2FkNjcyM2E5YzljNWFhNCIsImxpY2Vuc2VUeXBlIjoiU0lHTkFHRV9GSVJNIiwibmFtZSI6IkJyeWFuIEZhcm1lciIsIm9mZmljZXMiOm51bGwsInBob3RvIjoiIiwic3RhdHVzIjoiQWN0aXZlIiwidXNlclR5cGUiOiJzaWduYWdlX2Zpcm0iLCJ2aWQiOjB9.Un69MLjTkCIRioPWvJDNf7URdD3wARweeHLWuFSL-80"

//   const res = await fetch(`${process.env.REACT_APP_API_URL}/shipping/ups/configs`, {
//     headers: { Authorization: `Bearer ${token1}` }
//   });
//   // console.log(res,"fetchUpsIntegrations")
//   if (!res.ok) return [];
//   const data = await res.json().catch(() => []);
//   return Array.isArray(data) ? data : [];
// }

// export async function fetchUpsShipperAddresses(token: string) {
//             const token1 = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJlbWFpbCI6ImJyeWFuQGV4YW1wbGUuY29tIiwiZXhwIjoxNzYwMTEzMTY5LCJpZCI6IjY4Yjc3YmQ2N2FkNjcyM2E5YzljNWFhNCIsImxpY2Vuc2VUeXBlIjoiU0lHTkFHRV9GSVJNIiwibmFtZSI6IkJyeWFuIEZhcm1lciIsIm9mZmljZXMiOm51bGwsInBob3RvIjoiIiwic3RhdHVzIjoiQWN0aXZlIiwidXNlclR5cGUiOiJzaWduYWdlX2Zpcm0iLCJ2aWQiOjB9.Un69MLjTkCIRioPWvJDNf7URdD3wARweeHLWuFSL-80"

//   const res = await fetch(`${process.env.REACT_APP_API_URL}/shipping/ups/shipper-addresses`, {
//     headers: { Authorization: `Bearer ${token1}` }
//   });
//   // console.log(res,"fetchUpsShipperAddresses")
//   if (!res.ok) return [];
//   const data = await res.json().catch(() => []);
//   return Array.isArray(data) ? data : [];
// }

// // --- UPS: call rates -----------------------------------------------
// export async function fetchUpsRates(token: string, payload: {
//   integration_id: string;
//   shipper_address_id: string;
//   ship_to: UpsShipTo;
//   packages: Array<{ length: number; width: number; height: number; weight: number; unit: "LBS"; }>;
// }): Promise<UpsRateLine[]> {
//             const token1 = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJlbWFpbCI6ImJyeWFuQGV4YW1wbGUuY29tIiwiZXhwIjoxNzYwMTEzMTY5LCJpZCI6IjY4Yjc3YmQ2N2FkNjcyM2E5YzljNWFhNCIsImxpY2Vuc2VUeXBlIjoiU0lHTkFHRV9GSVJNIiwibmFtZSI6IkJyeWFuIEZhcm1lciIsIm9mZmljZXMiOm51bGwsInBob3RvIjoiIiwic3RhdHVzIjoiQWN0aXZlIiwidXNlclR5cGUiOiJzaWduYWdlX2Zpcm0iLCJ2aWQiOjB9.Un69MLjTkCIRioPWvJDNf7URdD3wARweeHLWuFSL-80"

//   const res = await fetch(`${process.env.REACT_APP_API_URL}/shipping/ups/rates`, {
//     method: "POST",
//     headers: { Authorization: `Bearer ${token1}`, "Content-Type": "application/json" },
//     body: JSON.stringify(payload),
//   });
//   // console.log(res,"fetchUpsRates")
//   if (!res.ok) return [];
//   const data = await res.json().catch(() => []);
//   return Array.isArray(data) ? data : [];
// }

// // --- UPS: map to your list shape -----------------------------------
// export function mapUpsRatesToShippingCosts(lines: UpsRateLine[]): MappedShippingCost[] {
//   return (lines || []).map((ln) => ({
//     _id: ln.service_code,
//     name: `UPS ${ln.service_name}`,
//     description: [
//       ln.delivery_days ? `${ln.delivery_days} business days` : "",
//       ln.delivery_by_time ? ln.delivery_by_time : ""
//     ].filter(Boolean).join(" • "),
//     cost: Number(ln.total_charge || ln.transportation || 0),
//     is_free: false,
//     is_taxable: true,
//     taxable: true,
//   }));
// }

// export function buildPackagesFromCart(cartItems: any[]) {
//   const toLbs = (w: number, unit?: string) => {
//     if (!Number.isFinite(w) || w <= 0) return 0;
//     const u = String(unit || "").trim().toLowerCase();
//     if (u === "g" || u === "gram" || u === "grams") return w / 453.59237;
//     if (u === "kg" || u === "kgs" || u === "kilogram" || u === "kilograms") return w * 2.20462262;
//     if (u === "oz" || u === "ounce" || u === "ounces") return w / 16;
//     return w; // assume lbs
//   };

//   const pkgs: Array<{ length: number; width: number; height: number; weight: number; unit: "LBS" }> = [];

//   (cartItems || []).forEach((row) => {
//     // priority: row.dimensions -> row.package_dimensions -> variant.package_dimensions -> product.package_dimensions
//     const variant =
//       Array.isArray(row.product?.variants)
//         ? row.product!.variants!.find((v: { variant_id: any; }) => v.variant_id === (row as any).variant_id) // if you store variant_id on row
//         : undefined;

//     const dims =
//       row.dimensions ||
//       row.package_dimensions ||
//       variant?.package_dimensions ||
//       row.product?.package_dimensions;

//     if (!dims) return;

//     const L = Number(dims.length);
//     const W = Number(dims.width);
//     const H = Number(dims.height);
//     const wLbs = toLbs(Number(dims.weight), dims.weight_type);

//     if (!(L > 0 && W > 0 && H > 0 && wLbs > 0)) return;

//     const qty = Math.max(1, Number((row as any).quantity || 1));
//     for (let i = 0; i < qty; i++) {
//       pkgs.push({ length: L, width: W, height: H, weight: wLbs, unit: "LBS" });
//     }
//   });

//   return pkgs; // [] if none had valid dimensions
// }



// // --- UPS: orchestrator used by your screen -------------------------
// export async function loadUpsLiveRates(
//   token: string,
//   params: { shipTo: UpsShipTo; cartItems: any[] }
// ): Promise<MappedShippingCost[]> {
//                 const token1 = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJlbWFpbCI6ImJyeWFuQGV4YW1wbGUuY29tIiwiZXhwIjoxNzYwMTEzMTY5LCJpZCI6IjY4Yjc3YmQ2N2FkNjcyM2E5YzljNWFhNCIsImxpY2Vuc2VUeXBlIjoiU0lHTkFHRV9GSVJNIiwibmFtZSI6IkJyeWFuIEZhcm1lciIsIm9mZmljZXMiOm51bGwsInBob3RvIjoiIiwic3RhdHVzIjoiQWN0aXZlIiwidXNlclR5cGUiOiJzaWduYWdlX2Zpcm0iLCJ2aWQiOjB9.Un69MLjTkCIRioPWvJDNf7URdD3wARweeHLWuFSL-80"

//   const [integs, shipperAddrs] = await Promise.all([
//     fetchUpsIntegrations(token1),
//     fetchUpsShipperAddresses(token1),
//   ]);

//   const integration_id = String(integs?.[0]?.id || integs?.[0]?._id || integs?.[0]?.integration_id || "");
//   // console.log(integration_id,"idintigrations")
//   const shipper_address_id = String(
//     shipperAddrs?.[0]?.shipper_address_id || shipperAddrs?.[0]?._id || shipperAddrs?.[0]?.id || ""
//   );
// //  console.log(shipper_address_id,"shipper_address_id")
//   if (!integration_id || !shipper_address_id) return [];

//   const packages = buildPackagesFromCart(params.cartItems);
//   const lines = await fetchUpsRates(token1   , {
//     integration_id,
//     shipper_address_id,
//     ship_to: params.shipTo,
//     packages,
//   });

//   return mapUpsRatesToShippingCosts(lines);
// }



// // Slaesortds and removal and installations code was below i was implemented by me


// /* ===============================
//  * TaxCloud v2 helpers (add-only)
//  * =============================== */

// // Safe non-empty id for fee lines (your API rejects empty itemId)
// export const TAXCLOUD_SPECIAL_FEE_ITEM_ID_V2 = "Special Fee";

// // Lightweight address type for these helpers (keeps old types intact)
// export type TaxCloudAddressV2 = {
//   addressLine1: string;
//   city: string;
//   state: string; // 2-letter preferred
//   zip: string;
// };

// type CartLineV2 = {
//   itemId: string;
//   price: number;
//   quantity: number;
//   itemType: "default" | "shipping" | "special_fee";
// };

// type UiFeeV2 = {
//   id?: string;
//   amount: number;
//   taxable?: boolean;
// };

// /**
//  * Fetch default warehouse origin (reuses your existing implementation if exported).
//  * If you already have fetchDefaultWarehouseAddress, we’ll call it.
//  */
// async function _getOriginV2(token: string): Promise<TaxCloudAddressV2> {
//   // @ts-ignore – prefer existing export if present
//   if (typeof fetchDefaultWarehouseAddress === "function") {
//     // @ts-ignore
//     const o = await fetchDefaultWarehouseAddress(token);
//     if (o && o.addressLine1) return o as TaxCloudAddressV2;
//   }
//   throw new Error("No default warehouse address found");
// }

// /**
//  * Core upsert (v2): posts to created-by/{supplierId} with optional cartNumber
//  */
// export async function taxcloudUpsertCart_v2(params: {
//   token: string;
//   supplierId: string;              // goes in URL
//   customerId?: string;             // defaults to supplierId
//   destination: TaxCloudAddressV2;
//   cartItems: CartLineV2[];
//   orderNumber?: string;            // sent as cartNumber
//   originOverride?: TaxCloudAddressV2 | null;
// }) {
//   const {
//     token,
//     supplierId,
//     destination,
//     cartItems,
//     orderNumber,
//     originOverride,
//   } = params;

//   const customerId = params.customerId || supplierId;
//   const origin = originOverride || (await _getOriginV2(token));

//   const payload: any = {
//     customerId,
//     origin,
//     destination,
//     cartItems,
//   };
//   if (orderNumber && typeof orderNumber === "string") {
//     payload.cartNumber = String(orderNumber); // must be a string
//   }

//   const res = await fetch(
//     `${process.env.REACT_APP_API_URL}/taxcloud/carts/created-by/${supplierId}`,
//     {
//       method: "POST",
//       headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
//       body: JSON.stringify(payload),
//     }
//   );

//   const text = await res.text();
//   let json: any = null;
//   try { json = text ? JSON.parse(text) : null; } catch {}
//   if (!res.ok) throw new Error(json?.message || json?.error || text || "Upsert cart failed");
//   return json ?? {};
// }

// /**
//  * SALES: products + shipping + taxable fees
//  */
// export async function taxcloudUpsertForSales_v2(params: {
//   token: string;
//   supplierId: string;              // cartId in URL
//   destination: TaxCloudAddressV2;
//   products: { sku: string; price: number; quantity: number }[];
//   shipping?: { id?: string; cost?: number } | null;
//   fees?: UiFeeV2[];                // pass only selected fees
//   orderNumber?: string;
//   originOverride?: TaxCloudAddressV2 | null;
// }) {
//   const { token, supplierId, destination, products, shipping, fees, orderNumber, originOverride } = params;

//   const productLines: CartLineV2[] = (products || []).map((p) => ({
//     itemId: String(p.sku || "SKU"),
//     price: Number(p.price || 0),
//     quantity: Number(p.quantity || 1),
//     itemType: "default",
//   }));

//   const shippingLine: CartLineV2[] =
//     shipping && Number(shipping.cost) > 0
//       ? [{
//           itemId: shipping.id || "SHIP001",
//           price: Number(shipping.cost || 0),
//           quantity: 1,
//           itemType: "shipping",
//         }]
//       : [];

//   const feeLines: CartLineV2[] = (fees || [])
//     .filter((f) => f && f.taxable !== false && Number(f.amount) > 0)
//     .map((f) => ({
//       itemId: f.id && f.id.trim() ? f.id : TAXCLOUD_SPECIAL_FEE_ITEM_ID_V2,
//       price: Number(f.amount),
//       quantity: 1,
//       itemType: "special_fee",
//     }));

//   return taxcloudUpsertCart_v2({
//     token,
//     supplierId,
//     customerId: supplierId,
//     destination,
//     cartItems: [...productLines, ...shippingLine, ...feeLines],
//     orderNumber,
//     originOverride: originOverride || null,
//   });
// }

// /**
//  * INSTALL / REMOVAL: single synthetic product line (installation_cost),
//  * itemId = first non-empty sku from line_items, + one fee line with non-empty id.
//  */
// export async function taxcloudUpsertForInstallRemoval_v2(params: {
//   token: string;
//   supplierId: string;              // cartId in URL
//   destination: TaxCloudAddressV2;
//   installationCost: number;        // order.installation_cost
//   specialFeeAmount?: number;       // order.special_fee_amount
//   productSkus?: string[];          // to choose product itemId
//   orderNumber?: string;
//   originOverride?: TaxCloudAddressV2 | null;
// }) {
//   const {
//     token, supplierId, destination, installationCost,
//     specialFeeAmount = 0, productSkus = [], orderNumber, originOverride,
//   } = params;

//   const lines: CartLineV2[] = [];

//   const productItemId =
//     productSkus.find((s) => typeof s === "string" && s.trim().length > 0) || "INSTALLATION_COST";

//   if (Number(installationCost) > 0) {
//     lines.push({
//       itemId: productItemId,
//       price: Number(installationCost),
//       quantity: 1,
//       itemType: "default",
//     });
//   }

//   if (Number(specialFeeAmount) > 0) {
//     lines.push({
//       itemId: TAXCLOUD_SPECIAL_FEE_ITEM_ID_V2, // non-empty id required by API
//       price: Number(specialFeeAmount),
//       quantity: 1,
//       itemType: "special_fee",
//     });
//   }

//   return taxcloudUpsertCart_v2({
//     token,
//     supplierId,
//     customerId: supplierId,
//     destination,
//     cartItems: lines,
//     orderNumber,
//     originOverride: originOverride || null,
//   });
// }

// /**
//  * REPORT (commit): must send TaxCloud cartId returned by carts API.
//  * Optionally include your human-readable orderNumber.
//  */
// export async function taxcloudCreateOrder_v2(
//   token: string,
//   supplierId: string,      // URL path created-by/{supplierId}
//   taxcloudCartId: string,  // resp.items[0].cartId from upsert
//   orderNumber?: string
// ) {
//   const body: any = { cartId: String(taxcloudCartId) };
//   if (orderNumber) body.orderId = String(orderNumber);

//   const res = await fetch(
//     `${process.env.REACT_APP_API_URL}/taxcloud/orders/created-by/${supplierId}`,
//     {
//       method: "POST",
//       headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
//       body: JSON.stringify(body),
//     }
//   );

//   const text = await res.text();
//   let json: any = null;
//   try { json = text ? JSON.parse(text) : null; } catch {}
//   if (!res.ok) throw new Error(json?.detail || json?.message || text || "TaxCloud order creation failed");
//   return json ?? {};
// }

// /** optional: re-export your ping to keep naming consistent */
// export async function taxcloudPing_v2(token: string): Promise<boolean> {
//   try {
//     // @ts-ignore – reuse your existing ping if available
//     if (typeof pingTaxCloud === "function") return await pingTaxCloud(token);
//   } catch {}
//   try {
//     const supplierId = localStorage.getItem("supplierId") || getAuthCookie("userId") || "";
//     const res = await fetch(`${process.env.REACT_APP_API_URL}/taxcloud/config/created-by/${supplierId}`, {
//       method: "GET",
//       headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
//     });
//     return res.ok;
//   } catch {
//     return false;
//   }
// }


// // ✅ Add (or replace) this implementation
// export async function completeTaxCloudOrderV2(
//   token: string,
//   supplierId: string,
//   orderId: string,
//   completedDateISO?: string
// ) {
//   const url = `${process.env.REACT_APP_API_URL}/taxcloud/orders/${encodeURIComponent(
//     orderId
//   )}/complete/by-created/${encodeURIComponent(supplierId)}`;

//   const res = await fetch(url, {
//     method: "PATCH",
//     headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
//     body: JSON.stringify({ completedDate: completedDateISO || new Date().toISOString() }),
//   });

//   const text = await res.text();
//   let json: any = null;
//   try { json = text ? JSON.parse(text) : null; } catch {}
//   if (!res.ok) {
//     throw new Error(json?.detail || json?.message || text || "TaxCloud order complete failed");
//   }
//   return json ?? {};
// }


// // specialfee data get useing below calls


// // --- User-based Special Fee fetcher ------------------------------------
// // pass the final selected person id (office / agent / office-agent) as userId
// export async function fetchUserbaseSpeicalfee(token: string, userId: string) {
//   // NOTE: per your request this endpoint uses a firm token; keeping as-is.
//   const token1 =
//     "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJlbWFpbCI6ImJyeWFuQGV4YW1wbGUuY29tIiwiZXhwIjoxNzYwMTEzMTY5LCJpZCI6IjY4Yjc3YmQ2N2FkNjcyM2E5YzljNWFhNCIsImxpY2Vuc2VUeXBlIjoiU0lHTkFHRV9GSVJNIiwibmFtZSI6IkJyeWFuIEZhcm1lciIsIm9mZmljZXMiOm51bGwsInBob3RvIjoiIiwic3RhdHVzIjoiQWN0aXZlIiwidXNlclR5cGUiOiJzaWduYWdlX2Zpcm0iLCJ2aWQiOjB9.Un69MLjTkCIRioPWvJDNf7URdD3wARweeHLWuFSL-80";

//   if (!userId) return [];
//   const res = await fetch(`${process.env.REACT_APP_API_URL}/special-fees/by-user/${userId}`, {
//     method: "GET",
//     headers: { Authorization: `Bearer ${token1}`, "Content-Type": "application/json" },
//   });
//   if (!res.ok) return [];
//   const data = await res.json().catch(() => []);
//   return Array.isArray(data) ? data : [];
// }

// // friendly alias (correct spelling)
// export const fetchUserbaseSpecialFee = fetchUserbaseSpeicalfee;


// // 
// // removal and installtion manually tax calculation

// export async function pingTaxCloudinstalltionandremovals(token: string) {
//   const res = await fetch(`${process.env.REACT_APP_API_URL}/taxcloud/ping`, {
//     headers: { Authorization: `Bearer ${token}` },
//   });
//   return res.ok;
// }

// /** manual tax when taxcloud is unavailable, respecting is_taxable flag */
// export function calcManualRemovalTaxes(params: {
//   removalCost: number;                 // base
//   specialFeeBreakdown: { amount: number; taxable?: boolean }[];
//   isTaxable: boolean;
//   ratePct: number;                     // e.g. 6
// }) {
//   const rate = Number(params.ratePct || 0) / 100;
//   const base = Number(params.removalCost || 0);

//   const productTax = params.isTaxable ? base * rate : 0;

//   const feeTaxablePart = params.specialFeeBreakdown
//     .filter(f => !!f.taxable)
//     .reduce((n, f) => n + Number(f.amount || 0), 0);
//   const specialFeeTax = params.isTaxable ? feeTaxablePart * rate : 0;

//   const totalTax = productTax + specialFeeTax;
//   const specialFeeTaxPct = params.isTaxable ? Number(params.ratePct || 0) : 0;

//   return {
//     productTaxAmount: Number(productTax.toFixed(2)),
//     specialFeeTaxAmount: Number(specialFeeTax.toFixed(2)),
//     totalTaxAmount: Number(totalTax.toFixed(2)),
//     productTaxPercentage: params.isTaxable ? Number(params.ratePct || 0) : 0,
//     specialFeeTaxPercentage: specialFeeTaxPct,
//   };
// }
